import React, { useState, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { ProjectCardMenu, MenuAction } from '@shared/components/ProjectCardMenu/ProjectCardMenu';
import { ProjectCardLabels, Label } from '@shared/components/ProjectCardLabels/ProjectCardLabels';
import {
  formatRelativeTime,
  formatLocation,
  BaseProjectProps,
  defaultProjectTranslations,
  getAvailableProjectMenuActions
} from '@shared/components/ProjectCard/utils';
import userImage from '@shared/assets/icon-user.svg';
import teamImage from '@shared/assets/icon-team.svg';
import defaultProjectImage from '@shared/assets/Logo-symbol-dark.svg';
import styles from './ProjectList.module.css';

// Re-export types for convenience
export type { Label };
export type { MenuAction };

/**
 * ProjectList - Horizontal bar component for displaying project information
 * Fixed height: 125px with image on left, content in center, menu on right
 */
export const ProjectList: React.FC<BaseProjectProps> = ({
  user,
  id,
  name,
  client,
  size,
  location,
  labels = [],
  updatedAt,
  members,
  type = 0,
  image,
  onClick,
  menuActions,
  onMenuAction,
  translations,
  notEntered = ''
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Merge provided translations with defaults
  const t = { ...defaultProjectTranslations, ...translations };

  // Set default menu actions using translations
  // Only show upgrade option for private projects (type === 0)
  const defaultMenuActions: MenuAction[] = [
    { id: 'edit', label: t.menuEdit!, icon: 'edit' },
    { id: 'delete', label: t.menuDelete!, icon: 'delete' },
    ...(type === 0 ? [{ id: 'upgrade', label: t.menuUpgrade!, icon: 'upgrade' }] : [])
  ];

  const availableMenuActions = getAvailableProjectMenuActions(
    menuActions || defaultMenuActions,
    members,
    user
  );

  const locationText = formatLocation(location);
  const updatedAtText = formatRelativeTime(updatedAt, t.relativeTime);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleMenuAction = (actionId: string) => {
    setIsMenuOpen(false);
    onMenuAction?.(id, actionId);
  };

  // Sort labels alphabetically
  const sortedLabels = useMemo(() => {
    return [...labels].sort((a, b) => {
      // Handle both string labels and object labels with .label property
      const labelA = (typeof a === 'string' ? a : a?.label || '').trim().toLowerCase();
      const labelB = (typeof b === 'string' ? b : b?.label || '').trim().toLowerCase();
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    });
  }, [labels]);

  // Check if using default project image for styling
  const isDefaultImage = !image?.url || image.url === defaultProjectImage || imageError;

  return (
    <article
      data-test="project-list"
      className={clsx(styles.container, onClick && styles.containerClickable)}
      onClick={onClick}
    >
      <div className={styles.inner}>
        {/* Image Container */}
        <div
          data-test="project-list-image"
          className={clsx(
            styles.imageContainer,
            isDefaultImage && styles.imageContainerDefault
          )}
        >
          <img
            src={(image?.url && !imageError) ? image.url : defaultProjectImage}
            alt={name}
            onError={() => setImageError(true)}
            className={clsx(
              styles.image,
              isDefaultImage && styles.defaultImage
            )}
          />
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Title Container */}
          <div className={styles.titleContainer}>
            <div className={styles.titleWrapper}>
              {Boolean(type) && (
                <img
                  src={teamImage}
                  alt="Team project"
                  className={styles.typeIcon}
                />
              )}
              {!Boolean(type) && (
                <img
                  src={userImage}
                  alt="Private project"
                  className={styles.typeIcon}
                />
              )}
              <h1 className={styles.title} title={name} data-test="project-list-title">{name}</h1>
            </div>
            <span className={styles.lastEdited}>{updatedAtText}</span>
            <ProjectCardLabels labels={sortedLabels} noPadding={true} data-test="project-list-labels" />
          </div>

          {/* Details */}
          <div className={styles.details} data-test="project-list-details">
            <div className={styles.detailGroup}>
              <div className={styles.detail}>
                <span className={styles.detailLabel}>{t.clientLabel}:</span>
                <span
                  className={clsx(
                    styles.detailValue,
                    !client && styles.detailValueEmpty
                  )}
                >
                  {client || notEntered}
                </span>
              </div>
              <div className={styles.detail}>
                <span className={styles.detailLabel}>{t.locationLabel}:</span>
                <span
                  className={clsx(
                    styles.detailValue,
                    !formatLocation(location) && styles.detailValueEmpty
                  )}
                >
                  {locationText || notEntered}
                </span>
              </div>
              <div className={styles.detail}>
                <span className={styles.detailLabel}>{t.sizeLabel}:</span>
                <span
                  className={clsx(
                    styles.detailValue,
                    !size && styles.detailValueEmpty
                  )}
                >
                  {size ? size + ' m2' : notEntered}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Button */}
      <div ref={menuRef} className={styles.menuContainer}>
        {!isMenuOpen && (
          <button
            data-test="project-list-menu-button"
            className={styles.menuButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(true);
            }}
            aria-label="More options"
          >
            <span 
              className={clsx('material-icons', styles.menuIcon)}
            >
              more_vert
            </span>
          </button>
        )}

        {/* Dropdown Menu */}
        <ProjectCardMenu
          isOpen={isMenuOpen}
          actions={availableMenuActions}
          onClose={() => setIsMenuOpen(false)}
          onActionClick={handleMenuAction}
          variant="list"
        />
      </div>
      
      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: scale(0.5);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </article>
  );
};
